import { useEffect, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Select, Table } from '@mantine/core'
import { fetchDevicePayments, fetchDevices } from '../../admin/server-fns'
import type { SerializedPaymentsResponse } from '../../admin/server-fns'
import {
  AdminRouteError,
  PageHeader,
  Pagination,
  Panel,
  Spinner,
  StatusBadge,
  formatDate,
  tdCls,
  thCls,
  totalPages,
} from '../../admin/ui'

/* The API exposes payments per device, so this page browses one device at a time. */

export const Route = createFileRoute('/admin/payments')({
  loader: async () => fetchDevices({ data: { page: 1, limit: 100 } }),
  component: Payments,
  errorComponent: AdminRouteError,
})

function Payments() {
  const devices = Route.useLoaderData()
  const [deviceId, setDeviceId] = useState<string | null>(devices.items[0]?.id ?? null)
  const [statusFilter, setStatusFilter] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [data, setData] = useState<SerializedPaymentsResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!deviceId) return
    setLoading(true)
    setError(null)
    fetchDevicePayments({
      data: {
        deviceId,
        params: { page, limit: 20, ...(statusFilter ? { status: statusFilter } : {}) },
      },
    })
      .then(setData)
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : 'Could not load payments.'),
      )
      .finally(() => setLoading(false))
  }, [deviceId, page, statusFilter])

  return (
    <>
      <PageHeader kicker="Operations" title="Payments" />

      {devices.items.length === 0 ? (
        <Panel className="px-4 py-12 text-center">
          <p className="m-0 text-sm text-slate-500 dark:text-slate-400">
            Payments are recorded per device, and there are no devices yet.
          </p>
        </Panel>
      ) : (
        <>
          <div className="mb-4 flex flex-wrap gap-2">
            <Select
              className="w-full max-w-xs"
              classNames={{ input: 'font-mono text-xs' }}
              searchable
              data={devices.items.map((d) => ({ value: d.id, label: d.serialNumber }))}
              value={deviceId}
              onChange={(value) => {
                setDeviceId(value)
                setPage(1)
              }}
              allowDeselect={false}
            />
            <Select
              placeholder="All statuses"
              data={['pending', 'paid', 'failed', 'refunded']}
              value={statusFilter}
              onChange={(value) => {
                setStatusFilter(value)
                setPage(1)
              }}
              clearable
            />
          </div>

          {error ? (
            <Panel className="border-red-300 px-4 py-3 text-sm text-red-700 dark:border-red-500/40 dark:text-red-400">
              {error}
            </Panel>
          ) : loading || !data ? (
            <Panel>
              <Spinner />
            </Panel>
          ) : data.items.length === 0 ? (
            <Panel className="px-4 py-12 text-center">
              <p className="m-0 text-sm text-slate-500 dark:text-slate-400">
                No payments on this device{statusFilter ? ` with status “${statusFilter}”` : ''}.
              </p>
            </Panel>
          ) : (
            <>
              <p className="mb-3 mt-0 font-mono text-xs text-slate-400 dark:text-slate-500">
                {data.total} payment{data.total === 1 ? '' : 's'}
              </p>
              <Panel>
                <Table.ScrollContainer minWidth={860}>
                  <Table highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th className={thCls}>Payment</Table.Th>
                        <Table.Th className={thCls}>Amount</Table.Th>
                        <Table.Th className={thCls}>Status</Table.Th>
                        <Table.Th className={thCls}>Site</Table.Th>
                        <Table.Th className={thCls}>Paid</Table.Th>
                        <Table.Th className={thCls}>Created</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {data.items.map((p) => (
                        <Table.Tr key={p.id}>
                          <Table.Td className={`${tdCls} font-mono text-xs`}>{p.id}</Table.Td>
                          <Table.Td className={`${tdCls} font-mono text-xs`}>
                            {p.amount} {p.currency}
                          </Table.Td>
                          <Table.Td className={tdCls}>
                            <StatusBadge status={p.status} />
                          </Table.Td>
                          <Table.Td className={`${tdCls} font-mono text-xs`}>
                            {p.siteId ?? '—'}
                          </Table.Td>
                          <Table.Td className={`${tdCls} font-mono text-xs`}>
                            {formatDate(p.paidAt)}
                          </Table.Td>
                          <Table.Td className={`${tdCls} font-mono text-xs`}>
                            {formatDate(p.createdAt)}
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Table.ScrollContainer>
              </Panel>
              <div className="mt-4">
                <Pagination
                  page={page}
                  pages={totalPages(data.total, data.limit)}
                  onChange={setPage}
                />
              </div>
            </>
          )}
        </>
      )}
    </>
  )
}
